import {
    MarketResearchCompensationData,
    MarketResearchDiversityData,
    MarketResearchShareData,
    MarketResearchSummaryData,
    MarketResearchSupplyDemandData,
    RelodeApiError,
    SuccessAction,
} from "@relode/core";
import {
    ActionsUnion,
    createAction,
    createMetaAction,
} from "@relode/core/components/reducer/actionHelpers";

import {
    JobCandidatesFunnel,
    JobDetails,
    JobEngagementFunnel,
    JobShareDetails,
    JobStats,
    TeamMemberSuggestion,
} from "../../../shared/types";

enum names {
    LOAD_JOB = "LOAD_JOB",
    LOAD_JOB_SUCCESS = "LOAD_JOB_SUCCESS",
    LOAD_JOB_FAILURE = "LOAD_JOB_FAILURE",

    LOAD_JOB_SHARE = "LOAD_JOB_SHARE",
    LOAD_JOB_SHARE_SUCCESS = "LOAD_JOB_SHARE_SUCCESS",
    LOAD_JOB_SHARE_FAILURE = "LOAD_JOB_SHARE_FAILURE",

    LOAD_TEAM_MEMBER_SUGGESTIONS = "LOAD_TEAM_MEMBER_SUGGESTIONS",
    LOAD_TEAM_MEMBER_SUGGESTIONS_SUCCESS = "LOAD_TEAM_MEMBER_SUGGESTIONS_SUCCESS",
    LOAD_TEAM_MEMBER_SUGGESTIONS_FAILURE = "LOAD_TEAM_MEMBER_SUGGESTIONS_FAILURE",

    MANAGE_TEAM_MEMBER = "MANAGE_TEAM_MEMBER",
    MANAGE_TEAM_MEMBER_SUCCESS = "MANAGE_TEAM_MEMBER_SUCCESS",
    MANAGE_TEAM_MEMBER_FAILURE = "MANAGE_TEAM_MEMBER_FAILURE",

    UPDATE_JOB_STATUS = "UPDATE_JOB_STATUS",
    UPDATE_JOB_STATUS_SUCCESS = "UPDATE_JOB_STATUS_SUCCESS",
    UPDATE_JOB_STATUS_FAILURE = "UPDATE_JOB_STATUS_FAILURE",

    SET_SELECTED_TAB = "SET_SELECTED_TAB",
    CLEAR_ERROR = "CLEAR_ERROR",

    MARKET_RESEARCH_COMPENSATION = "MARKET_RESEARCH_COMPENSATION",
    MARKET_RESEARCH_COMPENSATION_SUCCESS = "MARKET_RESEARCH_COMPENSATION_SUCCESS",
    MARKET_RESEARCH_COMPENSATION_FAILURE = "MARKET_RESEARCH_COMPENSATION_FAILURE",

    MARKET_RESEARCH_SUPPLY_DEMAND = "MARKET_RESEARCH_SUPPLY_DEMAND",
    MARKET_RESEARCH_SUPPLY_DEMAND_SUCCESS = "MARKET_RESEARCH_SUPPLY_DEMAND_SUCCESS",
    MARKET_RESEARCH_SUPPLY_DEMAND_FAILURE = "MARKET_RESEARCH_SUPPLY_DEMAND_FAILURE",

    MARKET_RESEARCH_DIVERSITY = "MARKET_RESEARCH_DIVERSITY",
    MARKET_RESEARCH_DIVERSITY_SUCCESS = "MARKET_RESEARCH_DIVERSITY_SUCCESS",
    MARKET_RESEARCH_DIVERSITY_FAILURE = "MARKET_RESEARCH_DIVERSITY_FAILURE",

    MARKET_RESEARCH_SHARE = "MARKET_RESEARCH_SHARE",
    MARKET_RESEARCH_SHARE_SUCCESS = "MARKET_RESEARCH_SHARE_SUCCESS",
    MARKET_RESEARCH_SHARE_FAILURE = "MARKET_RESEARCH_SHARE_FAILURE",

    MARKET_RESEARCH_SUMMARY = "MARKET_RESEARCH_SUMMARY",
    MARKET_RESEARCH_SUMMARY_SUCCESS = "MARKET_RESEARCH_SUMMARY_SUCCESS",
    MARKET_RESEARCH_SUMMARY_FAILURE = "MARKET_RESEARCH_SUMMARY_FAILURE",

    LOAD_JOB_STATS = "LOAD_JOB_STATS",
    LOAD_JOB_STATS_SUCCESS = "LOAD_JOB_STATS_SUCCESS",
    LOAD_JOB_STATS_FAILURE = "LOAD_JOB_STATS_FAILURE",

    LOAD_JOB_CANDIDATES_FUNNEL = "LOAD_JOB_CANDIDATES_FUNNEL",
    LOAD_JOB_CANDIDATES_FUNNEL_SUCCESS = "LOAD_JOB_CANDIDATES_FUNNEL_SUCCESS",
    LOAD_JOB_CANDIDATES_FUNNEL_FAILURE = "LOAD_JOB_CANDIDATES_FUNNEL_FAILURE",

    LOAD_JOB_ENGAGEMENT_FUNNEL = "LOAD_ENGAGEMENT_FUNNEL",
    LOAD_JOB_ENGAGEMENT_FUNNEL_SUCCESS = "LOAD_JOB_ENGAGEMENT_FUNNEL_SUCCESS",
    LOAD_JOB_ENGAGEMENT_FUNNEL_FAILURE = "LOAD_JOB_ENGAGEMENT_FUNNEL_FAILURE",
}

export default names;

export const creators = {
    loadJob: () => createAction(names.LOAD_JOB),
    loadJobSuccess: (jobId: number, job: JobDetails) =>
        createAction(names.LOAD_JOB_SUCCESS, {
            jobId,
            job,
        }),
    loadJobFailure: (apiError: RelodeApiError) =>
        createAction(names.LOAD_JOB_FAILURE, { apiError }),

    loadJobShareLink: () => createAction(names.LOAD_JOB_SHARE),
    loadJobShareLinkSuccess: (
        jobShareDetails: JobShareDetails,
        jobId: number
    ) =>
        createAction(names.LOAD_JOB_SHARE_SUCCESS, {
            jobShareDetails,
            jobId,
        }),
    loadJobShareLinkFailure: (apiError: RelodeApiError) =>
        createAction(names.LOAD_JOB_SHARE_FAILURE, { apiError }),

    loadTeamMemberSuggestions: () =>
        createAction(names.LOAD_TEAM_MEMBER_SUGGESTIONS),
    loadTeamMemberSuggestionsSuccess: (suggestions: TeamMemberSuggestion[]) =>
        createAction(names.LOAD_TEAM_MEMBER_SUGGESTIONS_SUCCESS, {
            suggestions,
        }),
    loadTeamMemberSuggestionsFailure: (apiError: RelodeApiError) =>
        createAction(names.LOAD_TEAM_MEMBER_SUGGESTIONS_FAILURE, { apiError }),

    managerTeamMember: () => createAction(names.MANAGE_TEAM_MEMBER),
    manageTeamMemberSuccess: (message: string) =>
        createMetaAction(
            names.MANAGE_TEAM_MEMBER_SUCCESS,
            new SuccessAction(message),
            { message }
        ),
    manageTeamMemberFailure: (apiError: RelodeApiError) =>
        createAction(names.MANAGE_TEAM_MEMBER_FAILURE, { apiError }),

    updateJobStatus: () => createAction(names.UPDATE_JOB_STATUS),
    updateJobStatusSuccess: (jobDetails: JobDetails, message: string) =>
        createMetaAction(
            names.UPDATE_JOB_STATUS_SUCCESS,
            new SuccessAction(message),
            { jobDetails }
        ),
    updateJobStatusFailure: (apiError: RelodeApiError) =>
        createAction(names.UPDATE_JOB_STATUS_FAILURE, { apiError }),

    setSelectedTab: (tab: string) =>
        createAction(names.SET_SELECTED_TAB, { tab }),
    clearError: () => createAction(names.CLEAR_ERROR),

    loadMarketResearchCompensation: () =>
        createAction(names.MARKET_RESEARCH_COMPENSATION),
    loadMarketResearchCompensationSuccess: (
        data: MarketResearchCompensationData
    ) => createAction(names.MARKET_RESEARCH_COMPENSATION_SUCCESS, { data }),

    loadMarketResearchCompensationFailure: (error: RelodeApiError) =>
        createAction(names.MARKET_RESEARCH_COMPENSATION_FAILURE, { error }),

    loadMarketResearchSupplyDemand: () =>
        createAction(names.MARKET_RESEARCH_SUPPLY_DEMAND),
    loadMarketResearchSupplyDemandSuccess: (
        data: MarketResearchSupplyDemandData
    ) => createAction(names.MARKET_RESEARCH_SUPPLY_DEMAND_SUCCESS, { data }),
    loadMarketResearchSupplyDemandFailure: (error: RelodeApiError) =>
        createAction(names.MARKET_RESEARCH_SUPPLY_DEMAND_FAILURE, { error }),

    loadMarketResearchDiversity: () =>
        createAction(names.MARKET_RESEARCH_DIVERSITY),
    loadMarketResearchDiversitySuccess: (data: MarketResearchDiversityData) =>
        createAction(names.MARKET_RESEARCH_DIVERSITY_SUCCESS, { data }),
    loadMarketResearchDiversityFailure: (error: RelodeApiError) =>
        createAction(names.MARKET_RESEARCH_DIVERSITY_FAILURE, { error }),

    loadMarketResearchShare: () => createAction(names.MARKET_RESEARCH_SHARE),
    loadMarketResearchShareSuccess: (data: MarketResearchShareData) =>
        createAction(names.MARKET_RESEARCH_SHARE_SUCCESS, {
            data,
        }),
    loadMarketResearchShareFailure: (error: RelodeApiError) =>
        createAction(names.MARKET_RESEARCH_SHARE_FAILURE, { error }),

    loadMarketResearchSummary: () =>
        createAction(names.MARKET_RESEARCH_SUMMARY),
    loadMarketResearchSummarySuccess: (summary: MarketResearchSummaryData) =>
        createAction(names.MARKET_RESEARCH_SUMMARY_SUCCESS, { summary }),
    loadMarketResearchSummaryFailure: (error: RelodeApiError) =>
        createAction(names.MARKET_RESEARCH_SUMMARY_FAILURE, { error }),

    loadJobCandidatesFunnel: () =>
        createAction(names.LOAD_JOB_CANDIDATES_FUNNEL),
    loadJobCandidatesFunnelSuccess: (
        jobCandidatesFunnel: JobCandidatesFunnel
    ) =>
        createAction(names.LOAD_JOB_CANDIDATES_FUNNEL_SUCCESS, {
            jobCandidatesFunnel,
        }),
    loadJobCandidatesFunnelFailure: (apiError: RelodeApiError) =>
        createAction(names.LOAD_JOB_CANDIDATES_FUNNEL_FAILURE, { apiError }),

    loadJobEngagementFunnel: () =>
        createAction(names.LOAD_JOB_ENGAGEMENT_FUNNEL),
    loadJobEngagementFunnelSuccess: (
        jobEngagementFunnel: JobEngagementFunnel
    ) =>
        createAction(names.LOAD_JOB_ENGAGEMENT_FUNNEL_SUCCESS, {
            jobEngagementFunnel,
        }),
    loadJobEngagementFunnelFailure: (apiError: RelodeApiError) =>
        createAction(names.LOAD_JOB_ENGAGEMENT_FUNNEL_FAILURE, { apiError }),

    loadJobStats: () => createAction(names.LOAD_JOB_STATS),
    loadJobStatsSuccess: (jobStats: JobStats) =>
        createAction(names.LOAD_JOB_STATS_SUCCESS, { jobStats }),
    loadJobStatsFailure: (apiError: RelodeApiError) =>
        createAction(names.LOAD_JOB_STATS_FAILURE, { apiError }),
};

export type JobActionTypes = ActionsUnion<typeof creators>;
